/* Banking Portal · Wave-11 — Customer Deep Profile (Feb 2026).
   Single-customer 360° forensic dossier for sponsor banks.
   Route: /banking/customer-profile/:userId (deep link from forensics)
   Also rendered standalone at /banking/customer-profile with built-in search.
   5 tabs:
     1. Identity & Org
     2. Scorecard (24m history sparkline)
     3. Credit & Banking
     4. Sessions & Devices
     5. Risk Surface (related-party ring)
*/
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import {
  User, Building2, Search, ShieldCheck, ShieldAlert, FileCheck2, ScrollText,
  Banknote, Activity, MonitorSmartphone, Network, AlertTriangle, FileWarning,
  TrendingUp, Globe, Hash, MapPin, Briefcase, Eye, CheckCircle2, XCircle,
  Clock, ArrowDownLeft, ArrowUpRight, ChevronRight,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

// ── helpers ──────────────────────────────────────────────────────────
function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function dateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function ts(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function ago(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const toneMap = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    violet:  "bg-violet-50 border-violet-200",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${toneMap[tone] || toneMap.slate} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusPill({ value, type }) {
  const map = {
    cip: {
      complete:   "bg-emerald-50 text-emerald-700 border-emerald-200",
      incomplete: "bg-amber-50 text-amber-700 border-amber-200",
    },
    cdd: {
      verified:     "bg-emerald-50 text-emerald-700 border-emerald-200",
      needs_review: "bg-amber-50 text-amber-700 border-amber-200",
      pending:      "bg-slate-50 text-slate-600 border-slate-200",
      failed:       "bg-rose-50 text-rose-700 border-rose-200",
    },
    bool: {
      true:  "bg-emerald-50 text-emerald-700 border-emerald-200",
      false: "bg-slate-50 text-slate-500 border-slate-200",
    },
  };
  const cls = (map[type] || {})[String(value)] || "bg-slate-50 text-slate-600 border-slate-200";
  return <Badge variant="outline" className={`${cls} text-[10.5px]`}>{String(value || "—")}</Badge>;
}

// ── Sparkline (inline SVG, no deps) ──────────────────────────────────
function Sparkline({ points, width = 280, height = 60, color = "#0ea5e9" }) {
  if (!points || points.length < 2) {
    return <div className="text-xs text-slate-400 italic">Not enough history.</div>;
  }
  const xs = points.map((_, i) => i);
  const ys = points.map(p => p ?? 0);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const range = maxY - minY || 1;
  const stepX = width / (xs.length - 1);
  const toY = v => height - 4 - ((v - minY) / range) * (height - 8);
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x * stepX} ${toY(ys[i])}`).join(" ");
  return (
    <svg width={width} height={height} className="block">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {ys.map((y, i) => (
        <circle key={i} cx={i * stepX} cy={toY(y)} r="1.5" fill={color} />
      ))}
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION 1 · Identity & Org
// ═════════════════════════════════════════════════════════════════════
function IdentityOrgSection({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/partner/banking/customer-profile/${userId}/identity-org`)
      .then(r => setData(r.data));
  }, [userId]);
  if (!data) return <div className="text-sm text-slate-400">Loading identity…</div>;
  const { identity, jurisdiction, attestation, industry_risk, documents, doc_checklist, doc_completeness_pct, ubo_acceptance_trail } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="section-identity-org">
      {/* Identity card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> Identity</h3>
          <Badge variant="outline" className="text-[10.5px] capitalize">{identity.role}</Badge>
        </div>
        <dl className="grid grid-cols-2 gap-y-2 text-[12.5px]">
          <dt className="text-slate-500">Company</dt><dd className="text-slate-900">{identity.company}</dd>
          <dt className="text-slate-500">Name</dt><dd className="text-slate-900 font-mono">{identity.name_masked}</dd>
          <dt className="text-slate-500">Email</dt><dd className="text-slate-900 font-mono">{identity.email_masked}</dd>
          <dt className="text-slate-500">Phone</dt><dd className="text-slate-900 font-mono">{identity.phone_masked}</dd>
          <dt className="text-slate-500">DBA</dt><dd className="text-slate-900">{identity.dba || "—"}</dd>
          <dt className="text-slate-500">Website</dt><dd className="text-slate-900 truncate">{identity.website || "—"}</dd>
          <dt className="text-slate-500">KYC</dt><dd><StatusPill type="cdd" value={attestation.cdd} /></dd>
          <dt className="text-slate-500">Email verified</dt><dd><StatusPill type="bool" value={identity.email_verified} /></dd>
          <dt className="text-slate-500">Suspended</dt><dd><StatusPill type="bool" value={identity.suspended} /></dd>
          <dt className="text-slate-500">Onboarded</dt><dd className="text-slate-900">{dateOnly(identity.created_at)}</dd>
        </dl>
      </div>

      {/* Jurisdiction + industry */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-slate-400" /> Jurisdiction & Industry</h3>
        <dl className="grid grid-cols-2 gap-y-2 text-[12.5px]">
          <dt className="text-slate-500">Country</dt><dd className="text-slate-900">{jurisdiction.country || "—"}</dd>
          <dt className="text-slate-500">State</dt><dd className="text-slate-900">{jurisdiction.state || "—"}</dd>
          <dt className="text-slate-500">Incorporation</dt><dd className="text-slate-900">{jurisdiction.incorporation_state || "—"}</dd>
          <dt className="text-slate-500">Tax class</dt><dd className="text-slate-900">{jurisdiction.tax_classification || "—"}</dd>
          <dt className="text-slate-500">Address on file</dt><dd><StatusPill type="bool" value={jurisdiction.address_present} /></dd>
          <dt className="text-slate-500">NAICS</dt><dd className="text-slate-900">{industry_risk.naics || "—"}</dd>
          <dt className="text-slate-500">Industry</dt><dd className="text-slate-900">{industry_risk.industry || "—"}</dd>
          <dt className="text-slate-500">High-risk industry</dt>
          <dd>
            <Badge variant="outline" className={industry_risk.high_risk ? "bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]" : "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"}>
              {industry_risk.high_risk ? "yes" : "no"}
            </Badge>
          </dd>
        </dl>
      </div>

      {/* Attestation triple */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-slate-400" /> CIP / CDD / UBO</h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg border border-slate-200 px-3 py-3 bg-slate-50">
            <div className="text-[10.5px] uppercase text-slate-500 tracking-wider">CIP</div>
            <div className="mt-1.5"><StatusPill type="cip" value={attestation.cip} /></div>
          </div>
          <div className="rounded-lg border border-slate-200 px-3 py-3 bg-slate-50">
            <div className="text-[10.5px] uppercase text-slate-500 tracking-wider">CDD</div>
            <div className="mt-1.5"><StatusPill type="cdd" value={attestation.cdd} /></div>
          </div>
          <div className="rounded-lg border border-slate-200 px-3 py-3 bg-slate-50">
            <div className="text-[10.5px] uppercase text-slate-500 tracking-wider">UBO</div>
            <div className="mt-1.5"><StatusPill type="bool" value={attestation.ubo_attested} /></div>
          </div>
        </div>
        {ubo_acceptance_trail.length > 0 && (
          <div className="mt-3 text-[11.5px] text-slate-500">
            UBO last attested {dateOnly(ubo_acceptance_trail[0].created_at)} via {ubo_acceptance_trail[0].accepted_via || "—"}
          </div>
        )}
      </div>

      {/* Document checklist */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-slate-400" /> KYB Documents</h3>
          <div className="text-[11.5px] text-slate-500 tabular-nums">{doc_completeness_pct}% complete</div>
        </div>
        <div className="space-y-1.5">
          {doc_checklist.map(c => (
            <div key={c.doc_type} className="flex items-center gap-2 text-[12.5px]">
              {c.present
                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                : <XCircle className="w-3.5 h-3.5 text-rose-500" />}
              <span className="text-slate-700 capitalize">{c.doc_type.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
        {documents.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">Recent uploads</div>
            {documents.slice(0, 3).map(d => (
              <div key={d.id} className="text-[11.5px] text-slate-600 flex items-center gap-2">
                <Hash className="w-3 h-3 text-slate-400" /> {d.doc_type} · {d.size_kb}kb · {dateOnly(d.created_at)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION 2 · Scorecard
// ═════════════════════════════════════════════════════════════════════
function ScorecardSection({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/partner/banking/customer-profile/${userId}/scorecard`)
      .then(r => setData(r.data));
  }, [userId]);
  if (!data) return <div className="text-sm text-slate-400">Loading scorecard…</div>;
  const { score, role } = data;
  const historyPoints = (score.history_24m || []).map(h => h.score);

  return (
    <div className="space-y-4" data-testid="section-scorecard">
      {/* Score header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-400" />
            {role === "seller" ? "Seller Trust Score" : "Buyer Score v4"}
          </h3>
          {score.tier && <Badge variant="outline" className="text-[10.5px] capitalize">{score.tier}</Badge>}
        </div>
        <div className="flex items-end gap-4">
          <div>
            <div className="text-4xl font-bold tabular-nums text-slate-900">{score.current ?? "—"}</div>
            <div className="text-[10.5px] text-slate-500">computed {score.computed_at ? ago(score.computed_at) + " ago" : "—"}</div>
          </div>
          <div className="flex-1 flex justify-end">
            <Sparkline points={historyPoints} />
          </div>
        </div>
      </div>

      {/* Seller sub-scores */}
      {role === "seller" && Object.keys(score.sub_scores || {}).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Sub-scores ({score.methodology_version || "—"})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(score.sub_scores).map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                <div className="text-lg font-semibold tabular-nums text-slate-900 mt-0.5">{typeof v === "number" ? v.toFixed(1) : v}</div>
                {score.weights?.[k] != null && (
                  <div className="text-[10.5px] text-slate-500">weight {Math.round(score.weights[k] * 100)}%</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buyer snapshot stats */}
      {role === "buyer" && score.snapshot && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Buyer snapshot</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Credit limit" value={money(score.snapshot.credit_limit)} icon={Banknote} />
            <Kpi label="Lifetime volume" value={money(score.snapshot.total_volume)} icon={Activity} />
            <Kpi label="Invoices" value={fmt(score.snapshot.total_invoices)} icon={ScrollText} />
            <Kpi label="Overdue" value={fmt(score.snapshot.overdue_count)} tone={score.snapshot.overdue_count > 0 ? "amber" : "emerald"} icon={Clock} />
          </div>
        </div>
      )}

      {/* Recent score events */}
      {score.recent_events?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent score events</h3>
          <table className="w-full text-sm">
            <thead className="text-[10.5px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-1.5">When</th>
                <th className="text-left py-1.5">Kind</th>
                <th className="text-left py-1.5">Label</th>
                <th className="text-right py-1.5">Δ</th>
                <th className="text-left py-1.5">Counterparty</th>
              </tr>
            </thead>
            <tbody>
              {score.recent_events.map((e, i) => (
                <tr key={e.id || i} className="border-t border-slate-100">
                  <td className="py-1.5 text-[11.5px] text-slate-500">{ago(e.at)} ago</td>
                  <td className="py-1.5"><Badge variant="outline" className="text-[10.5px]">{e.kind || "—"}</Badge></td>
                  <td className="py-1.5 text-[12.5px] text-slate-700">{e.label || e.code || "—"}</td>
                  <td className={`py-1.5 text-right tabular-nums text-[12.5px] ${e.delta > 0 ? "text-emerald-600" : e.delta < 0 ? "text-rose-600" : "text-slate-500"}`}>
                    {e.delta > 0 ? "+" : ""}{e.delta ?? "—"}
                  </td>
                  <td className="py-1.5 text-[11.5px] text-slate-600">{e.seller_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION 3 · Credit & Banking
// ═════════════════════════════════════════════════════════════════════
function CreditBankingSection({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/partner/banking/customer-profile/${userId}/credit-banking`)
      .then(r => setData(r.data));
  }, [userId]);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const { wallet, payment_methods, plaid_links, ar_aging, velocity, top_counterparties, credit_apps } = data;

  return (
    <div className="space-y-4" data-testid="section-credit-banking">
      {/* Wallet + velocity strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi testid="kpi-wallet" label="Wallet balance" value={money(wallet.balance_usd)} icon={Banknote} tone="sky" />
        <Kpi testid="kpi-txns-24h" label="Txns · 24h" value={fmt(velocity.txns_24h)} icon={Activity} />
        <Kpi testid="kpi-txns-7d"  label="Txns · 7d"  value={fmt(velocity.txns_7d)}  icon={Activity} />
        <Kpi testid="kpi-max-amt"  label="Max · 90d"  value={money(velocity.max_amt_90d_usd)} icon={ArrowUpRight} />
        <Kpi testid="kpi-avg-amt"  label="Avg · 90d"  value={money(velocity.avg_amt_90d_usd)} icon={ArrowDownLeft} />
      </div>

      {/* AR aging */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">A/R Aging</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { k: "current",  label: "Current",   tone: "emerald" },
            { k: "d30",      label: "1-30 days", tone: "amber" },
            { k: "d60",      label: "31-60 days",tone: "amber" },
            { k: "d90_plus", label: "60+ days",  tone: "rose" },
          ].map(b => (
            <div key={b.k} className={`rounded-lg border px-3 py-3 ${
              b.tone === "emerald" ? "bg-emerald-50 border-emerald-200" :
              b.tone === "amber"   ? "bg-amber-50 border-amber-200" :
              "bg-rose-50 border-rose-200"
            }`}>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{b.label}</div>
              <div className="text-xl font-semibold tabular-nums text-slate-900 mt-1">{money(ar_aging[b.k]?.amount)}</div>
              <div className="text-[10.5px] text-slate-500">{fmt(ar_aging[b.k]?.count)} invoices</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment methods + Plaid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Banknote className="w-4 h-4 text-slate-400" /> Payment methods · masked</h3>
          {payment_methods.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No methods on file.</div>
          ) : (
            <div className="space-y-1.5">
              {payment_methods.map(pm => (
                <div key={pm.id} className="flex items-center justify-between text-[12.5px] border-b border-slate-100 py-1.5 last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10.5px] capitalize">{pm.type}</Badge>
                    <span className="text-slate-900">{pm.bank_name || pm.brand || "—"}</span>
                    <span className="text-slate-400 font-mono">···{pm.last4 || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10.5px]">
                    {pm.verified && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">verified</Badge>}
                    {pm.is_default && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">default</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Network className="w-4 h-4 text-slate-400" /> Plaid-linked accounts</h3>
          {plaid_links.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No Plaid links connected.</div>
          ) : (
            <div className="space-y-2">
              {plaid_links.map(p => (
                <div key={p.id} className="border border-slate-100 rounded-md p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-slate-900">{p.institution_name || "—"}</span>
                    <Badge variant="outline" className="text-[10.5px]">{p.env}</Badge>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-1">connected {dateOnly(p.connected_at)}</div>
                  <div className="mt-1.5 space-y-0.5">
                    {(p.accounts || []).map((a, i) => (
                      <div key={i} className="text-[11.5px] text-slate-600 font-mono">
                        {a.name} · {a.subtype || a.type || "—"} · ···{a.last4 || "—"}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top counterparties + credit apps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Top counterparties</h3>
          {top_counterparties.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No invoice activity.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {top_counterparties.map(c => (
                  <tr key={c.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 text-[12.5px] text-slate-900">{c.name}</td>
                    <td className="py-1.5 text-right text-[12.5px] text-slate-500 tabular-nums">{fmt(c.count)} inv</td>
                    <td className="py-1.5 text-right text-[12.5px] text-slate-900 tabular-nums">{money(c.amount_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Credit applications</h3>
          {credit_apps.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No applications.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-1.5">Submitted</th><th className="text-right py-1.5">Amount</th><th className="text-left py-1.5">Terms</th><th className="text-left py-1.5">Status</th></tr>
              </thead>
              <tbody>
                {credit_apps.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="py-1.5 text-[12.5px] text-slate-500">{dateOnly(a.created_at)}</td>
                    <td className="py-1.5 text-right tabular-nums text-[12.5px]">{money(a.amount)}</td>
                    <td className="py-1.5 text-[12.5px] text-slate-600">{a.terms}</td>
                    <td className="py-1.5"><Badge variant="outline" className="text-[10.5px] capitalize">{a.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION 4 · Sessions & Devices
// ═════════════════════════════════════════════════════════════════════
function SessionsDevicesSection({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/partner/banking/customer-profile/${userId}/sessions-devices`)
      .then(r => setData(r.data));
  }, [userId]);
  if (!data) return <div className="text-sm text-slate-400">Loading sessions…</div>;
  const { sessions, logins, active_sessions, failed_logins_24h, top_ips, impossible_travel } = data;

  return (
    <div className="space-y-4" data-testid="section-sessions-devices">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-active-sessions" label="Active sessions" value={fmt(active_sessions)} icon={MonitorSmartphone} tone={active_sessions > 0 ? "sky" : "slate"} />
        <Kpi testid="kpi-failed-24h" label="Failed logins · 24h" value={fmt(failed_logins_24h)} icon={AlertTriangle} tone={failed_logins_24h > 0 ? "amber" : "emerald"} />
        <Kpi testid="kpi-top-ips" label="Distinct IPs" value={fmt(top_ips.length)} icon={Globe} />
        <Kpi testid="kpi-impossible-travel" label="Impossible travel" value={fmt(impossible_travel.length)} icon={Network} tone={impossible_travel.length > 0 ? "rose" : "emerald"} />
      </div>

      {impossible_travel.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <h3 className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Impossible-travel events</h3>
          <div className="space-y-1.5">
            {impossible_travel.map((e, i) => (
              <div key={i} className="text-[12.5px] text-rose-800">
                <span className="font-mono">{e.from_ip}</span> → <span className="font-mono">{e.to_ip}</span> in {e.gap_min}min
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent sessions</h3>
          {sessions.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No active sessions.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1.5">IP</th>
                  <th className="text-left py-1.5">Device</th>
                  <th className="text-left py-1.5">Last active</th>
                  <th className="text-left py-1.5">State</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="py-1.5 font-mono text-[11.5px]">{s.ip || "—"}</td>
                    <td className="py-1.5 text-[11.5px] text-slate-700">{s.device || "—"}</td>
                    <td className="py-1.5 text-[11.5px] text-slate-500">{ago(s.last_active_at)} ago</td>
                    <td className="py-1.5"><StatusPill type="bool" value={!s.revoked} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent login attempts</h3>
          {logins.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No login attempts.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1.5">When</th>
                  <th className="text-left py-1.5">IP</th>
                  <th className="text-left py-1.5">Method</th>
                  <th className="text-left py-1.5">Result</th>
                </tr>
              </thead>
              <tbody>
                {logins.slice(0, 10).map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5 text-[11.5px] text-slate-500">{ago(l.created_at)} ago</td>
                    <td className="py-1.5 font-mono text-[11.5px]">{l.ip || "—"}</td>
                    <td className="py-1.5 text-[11.5px] text-slate-600">{l.method || "—"}</td>
                    <td className="py-1.5">
                      <Badge variant="outline" className={l.success
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"
                        : "bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]"}>
                        {l.success ? "ok" : (l.reason || "fail")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Top IPs by activity</h3>
        <div className="flex flex-wrap gap-2">
          {top_ips.map(t => (
            <Badge key={t.ip} variant="outline" className="text-[11.5px] font-mono">
              {t.ip} <span className="ml-1.5 text-slate-400">×{t.count}</span>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION 5 · Risk Surface
// ═════════════════════════════════════════════════════════════════════
function RiskSurfaceSection({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/partner/banking/customer-profile/${userId}/risk-surface`)
      .then(r => setData(r.data));
  }, [userId]);
  if (!data) return <div className="text-sm text-slate-400">Loading risk surface…</div>;
  const { tags, aml_alerts, sanctions, adverse_actions, complaints, email_events_30d, related_parties } = data;
  const ringToneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
  }[related_parties.ring_tone];

  return (
    <div className="space-y-4" data-testid="section-risk-surface">
      {/* Ring detection — front and center */}
      <div className={`rounded-xl border-2 px-5 py-4 ${ringToneCls}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Network className="w-4 h-4" /> Related-party ring score</h3>
          <div className="text-3xl font-bold tabular-nums">{related_parties.ring_score}</div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {related_parties.ring_flags.length === 0 && <span className="text-[11.5px]">No related-party signals detected.</span>}
          {related_parties.ring_flags.map(f => (
            <Badge key={f} variant="outline" className="text-[10.5px] font-mono bg-white/60">{f}</Badge>
          ))}
        </div>
      </div>

      {/* Related parties detail */}
      {(related_parties.same_ein.length > 0 || related_parties.same_company.length > 0 || related_parties.shared_ip_users.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {related_parties.same_ein.length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <h4 className="text-sm font-semibold text-rose-900 mb-2">Same EIN ({related_parties.same_ein.length})</h4>
              {related_parties.same_ein.map(p => (
                <div key={p.id} className="text-[11.5px] text-rose-800">{p.company} · <span className="font-mono">{p.email_masked}</span></div>
              ))}
            </div>
          )}
          {related_parties.same_company.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Same company name ({related_parties.same_company.length})</h4>
              {related_parties.same_company.map(p => (
                <div key={p.id} className="text-[11.5px] text-amber-800">{p.company} · <span className="font-mono">{p.email_masked}</span> {p.ein_match && <Badge variant="outline" className="ml-1 text-[10px]">EIN match</Badge>}</div>
              ))}
            </div>
          )}
          {related_parties.shared_ip_users.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Shared login IP ({related_parties.shared_ip_users.length})</h4>
              {related_parties.shared_ip_users.slice(0, 5).map(p => (
                <div key={p.id} className="text-[11.5px] text-amber-800">{p.company} <span className="text-amber-600">×{p.shared_login_count}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AML / Sanctions / AAN / Complaints rollup */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="AML alerts" value={fmt(aml_alerts.length)} icon={ShieldAlert} tone={aml_alerts.length > 0 ? "amber" : "emerald"} />
        <Kpi label="Sanctions screens" value={fmt(sanctions.length)} icon={Globe} />
        <Kpi label="Adverse actions" value={fmt(adverse_actions.length)} icon={FileWarning} tone={adverse_actions.length > 0 ? "amber" : "emerald"} />
        <Kpi label="Complaints" value={fmt(complaints.length)} icon={ScrollText} tone={complaints.length > 0 ? "amber" : "emerald"} />
      </div>

      {/* Detail tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">AML alerts</h3>
          {aml_alerts.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No alerts recorded.</div>
          ) : (
            <div className="space-y-1.5">
              {aml_alerts.map(a => (
                <div key={a.id} className="flex items-center justify-between text-[12.5px] border-b border-slate-100 py-1.5 last:border-0">
                  <span>{a.kind || "—"}</span>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className={`text-[10.5px] ${
                      a.severity === "critical" ? "bg-rose-50 text-rose-700 border-rose-200" :
                      a.severity === "high"     ? "bg-amber-50 text-amber-700 border-amber-200" :
                      "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {a.severity || "—"}
                    </Badge>
                    <span className="text-[10.5px] text-slate-500">{dateOnly(a.triggered_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Email delivery · 30d</h3>
          {Object.keys(email_events_30d).length === 0 ? (
            <div className="text-[12.5px] text-slate-400 italic">No email events on file.</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(email_events_30d).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-700 capitalize">{k}</span>
                  <span className="tabular-nums text-slate-900">{fmt(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Admin-applied tags</h3>
          <div className="flex flex-wrap gap-1.5">
            {tags.flatMap(t => (t.tags || []).map((tag, i) => (
              <Badge key={`${t.seller_id}-${i}`} variant="outline" className="text-[10.5px] bg-slate-50">{tag}</Badge>
            )))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SEARCH BAR (when no userId in path)
// ═════════════════════════════════════════════════════════════════════
function CustomerSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/partner/banking/forensics/users/search?limit=15&q=${encodeURIComponent(q)}`);
      setResults(r.data.rows || []);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const t = setTimeout(search, 280);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="customer-search">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Search className="w-4 h-4 text-slate-400" /> Search a customer</h3>
      <Input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Email, company, EIN, or user id…"
        data-testid="customer-search-input"
        className="text-sm"
      />
      <div className="mt-3 max-h-80 overflow-y-auto divide-y divide-slate-100">
        {busy && <div className="text-[12.5px] text-slate-400 py-2">Searching…</div>}
        {results.map(r => (
          <button
            key={r.id}
            onClick={() => onPick(r)}
            data-testid={`search-result-${r.id}`}
            className="w-full text-left py-2 px-1 hover:bg-slate-50 rounded flex items-center justify-between"
          >
            <div>
              <div className="text-[13px] text-slate-900 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />{r.company}
              </div>
              <div className="text-[10.5px] text-slate-500 font-mono">{r.email_masked} · {r.role}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </button>
        ))}
        {!busy && results.length === 0 && q.length > 0 && (
          <div className="text-[12.5px] text-slate-400 py-2">No results.</div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═════════════════════════════════════════════════════════════════════
export function BankingCustomerProfile() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const userId = params.userId || searchParams.get("userId") || null;

  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    if (!userId) { setSnapshot(null); return; }
    api.get(`/partner/banking/customer-profile/${userId}/snapshot`)
      .then(r => setSnapshot(r.data))
      .catch(() => setSnapshot({ error: true }));
  }, [userId]);

  // No customer chosen — render search
  if (!userId) {
    return (
      <div className="space-y-5" data-testid="banking-customer-profile-empty">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-500" /> Customer Deep Profile
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Pick a customer to view the full forensic dossier — identity, scorecard, banking, sessions, and risk surface.
          </p>
        </div>
        <CustomerSearch onPick={r => nav(`/banking/customer-profile/${r.id}`)} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="banking-customer-profile">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => nav("/banking/customer-profile")}
            data-testid="back-search-btn"
            className="text-xs h-7 px-2"
          >← Search</Button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-500" /> {snapshot?.user?.company || "Loading…"}
          </h1>
          {snapshot?.user?.suspended && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">suspended</Badge>}
          {snapshot?.industry_risk?.high_risk && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">high-risk industry</Badge>}
        </div>
        {snapshot?.user && (
          <p className="text-[13px] text-slate-500 mt-1 font-mono">
            {snapshot.user.role} · {snapshot.user.email_masked} · onboarded {dateOnly(snapshot.user.created_at)}
          </p>
        )}
      </div>

      {/* Snapshot strip */}
      {snapshot && !snapshot.error && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="snapshot-strip">
          <Kpi testid="snap-score" label={`${snapshot.score?.kind || ""} score`}
               value={snapshot.score?.value ?? "—"}
               sub={snapshot.score?.tier || ""}
               icon={TrendingUp}
               tone={snapshot.score?.value >= 80 ? "emerald" : snapshot.score?.value >= 60 ? "amber" : snapshot.score?.value != null ? "rose" : "slate"} />
          <Kpi testid="snap-wallet" label="Wallet" value={money(snapshot.wallet_balance_usd)} icon={Banknote} tone="sky" />
          <Kpi testid="snap-vol-in"  label="Volume in · 30d"  value={money(snapshot.volume_30d?.in_usd)} icon={ArrowDownLeft} />
          <Kpi testid="snap-vol-out" label="Volume out · 30d" value={money(snapshot.volume_30d?.out_usd)} icon={ArrowUpRight} />
          <Kpi testid="snap-open-inv" label="Open invoices" value={fmt(snapshot.open_invoices)} sub={money(snapshot.open_invoice_amount_usd)} icon={ScrollText} />
          <Kpi testid="snap-aml" label="Open AML" value={fmt(snapshot.open_aml_alerts)} icon={ShieldAlert}
               tone={snapshot.open_aml_alerts > 0 ? "amber" : "emerald"} />
        </div>
      )}

      <Tabs defaultValue="identity" className="w-full">
        <TabsList data-testid="profile-tabs" className="flex-wrap">
          <TabsTrigger value="identity"   data-testid="tab-identity">Identity & Org</TabsTrigger>
          <TabsTrigger value="scorecard"  data-testid="tab-scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="banking"    data-testid="tab-banking">Credit & Banking</TabsTrigger>
          <TabsTrigger value="sessions"   data-testid="tab-sessions">Sessions & Devices</TabsTrigger>
          <TabsTrigger value="risk"       data-testid="tab-risk">Risk Surface</TabsTrigger>
        </TabsList>
        <TabsContent value="identity"  className="mt-4"><IdentityOrgSection userId={userId} /></TabsContent>
        <TabsContent value="scorecard" className="mt-4"><ScorecardSection userId={userId} /></TabsContent>
        <TabsContent value="banking"   className="mt-4"><CreditBankingSection userId={userId} /></TabsContent>
        <TabsContent value="sessions"  className="mt-4"><SessionsDevicesSection userId={userId} /></TabsContent>
        <TabsContent value="risk"      className="mt-4"><RiskSurfaceSection userId={userId} /></TabsContent>
      </Tabs>
    </div>
  );
}
